import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { ShowcaseProject } from '../types/showcase';

const SCROLL_KEY = 'showcase_scroll_position';

const ShowcasePage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ShowcaseProject[]>([]);
  const [loading, setLoading] = useState(true);
  const restoredScroll = useRef(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (!loading && projects.length > 0 && !restoredScroll.current) {
      restoredScroll.current = true;
      const saved = sessionStorage.getItem(SCROLL_KEY);
      if (saved) {
        const scrollY = parseInt(saved, 10);
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });
        sessionStorage.removeItem(SCROLL_KEY);
      }
    }
  }, [loading, projects]);

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('showcase_projects')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;

      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const openProject = (project: ShowcaseProject) => {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    navigate(`/showcase/${project.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="pt-44 pb-20 lg:pt-52">
          <div className="max-w-5xl mx-auto px-6 text-center space-y-6">
            <div className="h-4 w-32 bg-white/[0.04] rounded mx-auto animate-pulse" />
            <div className="h-16 lg:h-20 w-[480px] max-w-full bg-white/[0.04] rounded-lg mx-auto animate-pulse" />
            <div className="h-6 w-[380px] max-w-full bg-white/[0.03] rounded mx-auto animate-pulse" />
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 pb-32 space-y-10">
          {[1, 2, 3].map(i => (
            <div key={i} className={`grid lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden bg-white/[0.02] border border-white/[0.05] w-full lg:max-w-[55%] ${i % 2 === 0 ? 'lg:ml-auto' : 'lg:mr-auto'}`}>
              <div className={`aspect-[16/10] lg:aspect-auto lg:min-h-[300px] bg-white/[0.03] animate-pulse ${i % 2 === 0 ? 'lg:order-2' : ''}`} />
              <div className={`p-8 lg:p-12 space-y-4 ${i % 2 === 0 ? 'lg:order-1' : ''}`}>
                <div className="h-3 w-20 bg-white/[0.04] rounded animate-pulse" />
                <div className="h-8 w-3/4 bg-white/[0.04] rounded animate-pulse" />
                <div className="h-4 w-full bg-white/[0.03] rounded animate-pulse" />
                <div className="h-4 w-2/3 bg-white/[0.03] rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <section className="relative pt-44 pb-24 lg:pt-52 lg:pb-32 overflow-hidden">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full pointer-events-none opacity-60"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(175,0,255,0.07) 0%, rgba(74,108,247,0.03) 40%, transparent 70%)',
          }}
        />

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-[0.8rem] text-gray-500 uppercase tracking-[0.25em] font-medium mb-8"
          >
            Selected Work
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl sm:text-7xl lg:text-[5.5rem] font-semibold tracking-[-0.03em] text-white leading-[1.05]"
          >
            Built Properly
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">.</span>
          </motion.h1>

        </div>
      </section>

      <section className="max-w-[90rem] mx-auto px-6 -mt-8 pb-24 lg:pb-32">
        <AnimatePresence mode="wait">
          <motion.div
            key="projects"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            {projects.length === 0 ? (
              <div className="py-24 text-center">
                <p className="text-gray-500 text-lg">No projects yet.</p>
                <p className="text-gray-600 text-sm mt-2">Check back soon.</p>
              </div>
            ) : (
              <div className="space-y-10">
                {projects.map((project, index) => {
                  const imageFirst = index % 2 === 0;
                  const pushRight = index % 2 === 0;

                  return (
                    <motion.div
                      key={project.id}
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-80px' }}
                      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.25, 0.1, 0.25, 1] }}
                      onClick={() => openProject(project)}
                      className={`group cursor-pointer w-full lg:max-w-[55%] ${pushRight ? 'lg:ml-auto' : 'lg:mr-auto'}`}
                    >
                      <div className={`grid lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden bg-white/[0.02] border border-white/[0.06] lg:hover:border-white/[0.12] transition-all duration-500 lg:hover:shadow-[0_8px_60px_rgba(175,0,255,0.05)]`}>
                        <div className={`relative aspect-[16/10] lg:aspect-auto lg:min-h-[300px] overflow-hidden ${!imageFirst ? 'lg:order-2' : ''}`}>
                          <img
                            src={project.main_image_url}
                            alt={project.title}
                            className={`w-full h-full lg:group-hover:scale-[1.03] transition-transform duration-700 ease-out ${project.image_fit === 'contain' ? 'object-contain' : 'object-cover'}`}
                            loading={index < 2 ? 'eager' : 'lazy'}
                          />
                        </div>
                        <div className={`p-6 lg:p-8 xl:p-10 flex flex-col justify-center ${!imageFirst ? 'lg:order-1' : ''}`}>
                          <span className="text-xs text-primary/80 uppercase tracking-[0.2em] font-medium">
                            {project.category}
                          </span>
                          <h2 className="mt-5 text-2xl lg:text-3xl xl:text-4xl font-semibold text-white tracking-tight leading-tight">
                            {project.title}
                          </h2>
                          {project.client_name && (
                            <p className="mt-3 text-sm text-gray-500">
                              {project.client_name}
                            </p>
                          )}
                          <p className="mt-5 text-gray-400 leading-relaxed">
                            {project.short_description}
                          </p>
                          <div className="mt-8 flex items-center gap-2 text-sm text-white/70 font-medium lg:group-hover:text-white transition-colors duration-300">
                            View Project
                            <ArrowUpRight className="h-4 w-4 lg:group-hover:translate-x-0.5 lg:group-hover:-translate-y-0.5 transition-transform duration-300" />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </section>

      <section className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="w-16 h-px bg-gradient-to-r from-primary/40 to-accent/40 mx-auto mb-12" />
            <p className="text-2xl lg:text-3xl text-gray-300 font-light tracking-tight leading-relaxed">
              Built for businesses that expect more.
            </p>
            <div className="w-16 h-px bg-gradient-to-r from-primary/40 to-accent/40 mx-auto mt-12" />
          </motion.div>
        </div>
      </section>

      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center bottom, rgba(175,0,255,0.04) 0%, transparent 60%)',
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative max-w-3xl mx-auto px-6 text-center"
        >
          <h2 className="text-3xl lg:text-5xl font-semibold text-white tracking-tight">
            Have a project in mind?
          </h2>
          <p className="mt-6 text-lg text-gray-400">
            Let's build something that actually works.
          </p>
          <div className="mt-10">
            <Link
              to="/contact"
              className="btn-primary inline-flex items-center gap-2.5 px-8 py-4 text-sm"
            >
              Start a Project
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default ShowcasePage;
